import { PRESTA_BENEFITS } from "data/constants";
import { motion, useAnimation, useInView } from "framer-motion";
import { useEffect, useRef } from "react";
import CallToActionHeader from "./CallToActionHeader";
import CallToFaqSection from "./CallToFaqSection";
import EventServicesCallToAction from "./HowItWorkCallToAction";
import PrestaCallToActionImage from "./PrestaCallToActionImage";
import { Helmet } from "react-helmet";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { FaCheck, FaRocket, FaStar, FaChartLine } from "react-icons/fa";

const PrestaCallToAction = () => {
  const benefitsRef = useRef(null);
  const callToActionImageRef = useRef(null);

  const benefitsControls = useAnimation();
  const callToActionImageControls = useAnimation();

  const isBenefitsInView = useInView(benefitsRef, { once: true, margin: "-100px" });
  const isCallToActionImageInView = useInView(callToActionImageRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isBenefitsInView) {
      benefitsControls.start("visible");
    }
  }, [isBenefitsInView, benefitsControls]);

  useEffect(() => {
    if (isCallToActionImageInView) {
      callToActionImageControls.start("visible");
    }
  }, [isCallToActionImageInView, callToActionImageControls]);

  return (
    <>
      <Helmet>
        <title>
          Prestataire événementiel Paris, Île-de-France | Trouvez les meilleurs services | Event To Bee{" "}
        </title>
        <meta
          name="description"
          content="Trouvez votre prestataire événementiel à Paris, Île-de-France  : traiteurs, musiciens, animateurs, photographes. Des services sur mesure pour vos événements professionnels et privés."
        />
      </Helmet>
      <div className="relative overflow-hidden bg-gradient-to-br from-neutral-50 via-white to-neutral-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
        {/* Background Effects */}
        <BgGlassmorphism />
        <div className="absolute -top-6 -left-6 w-96 h-96 bg-gradient-to-br from-primary-200/15 to-secondary-200/15 rounded-full blur-3xl animate-pulse"></div>

        <CallToActionHeader />

        <div ref={callToActionImageRef}>
          <PrestaCallToActionImage controls={callToActionImageControls} />
        </div>

        {/* Benefits Section - Modernized */}
        <motion.div
          className="relative px-4 py-20 overflow-hidden"
          ref={benefitsRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Hero Section for Benefits */}
          <div className="container mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-full text-sm font-medium text-pink-600 mb-6">
                <FaRocket className="w-4 h-4 mr-2 text-pink-500" />
                Devenez partenaire
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 dark:text-white mb-6">
                Pourquoi référencer vos services sur EVENTTOBEE
              </h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto leading-relaxed">
                Rejoignez notre réseau de professionnels et développez votre activité avec des clients qualifiés
              </p>
            </motion.div>

            {/* Benefits Grid */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {PRESTA_BENEFITS.map((value, index) => (
                <motion.div
                  key={value.id}
                  className="group relative bg-white dark:bg-neutral-800 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 border border-neutral-200 dark:border-neutral-700"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                >
                  {/* Icon Circle */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <img
                        src={value.icon}
                        alt={value.title}
                        width={40}
                        height={40}
                        className="filter brightness-0 invert"
                      />
                    </div>
                    {/* Decorative element */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-accent-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <FaCheck className="w-3 h-3 text-white m-1.5" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h5 className="text-xl font-bold text-neutral-900 dark:text-white mb-4 group-hover:text-primary-600 transition-colors duration-300">
                      {value.title}
                    </h5>
                    <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-6">
                      {value.description}
                    </p>

                    {/* Progress indicator */}
                    <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mb-4">
                      <div
                        className="bg-gradient-to-r from-pink-500 to-rose-500 h-2 rounded-full transition-all duration-1000 delay-500"
                        style={{ width: `${(index + 1) * 30}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-center text-sm text-neutral-500 dark:text-neutral-400">
                      <FaChartLine className="w-4 h-4 mr-2" />
                      <span>Impact sur votre activité</span>
                    </div>
                  </div>

                  {/* Hover Border Effect */}
                  <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-primary-500/50 transition-colors duration-500"></div>
                </motion.div>
              ))}
            </motion.div>

            {/* Call to Action */}
            <motion.div
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.6 }}
            >
              <div className="bg-gradient-to-r from-pink-500 via-pink-600 to-rose-500 rounded-3xl p-8 text-white max-w-4xl mx-auto">
                <div className="flex items-center justify-center mb-4">
                  <FaStar className="w-6 h-6 mr-2" />
                  <span className="text-lg font-semibold">Prêt à nous rejoindre ?</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">Commencez dès aujourd'hui</h3>
                <p className="text-lg mb-6 opacity-90">
                  Inscription gratuite et mise en ligne immédiate de vos services
                </p>
                <button className="bg-white text-pink-600 px-8 py-4 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg">
                  Créer mon profil prestataire
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <EventServicesCallToAction />
        <CallToFaqSection />
      </div>
    </>
  );
};

export default PrestaCallToAction;
